import { ThemeCompany, ThemeSchemaResponse } from '@api/api/theme-overview';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ThemeHeaderDetailed } from '@terminal/components/theme-header-detailed/ThemeHeaderDetailed';
import { useGraphRelations } from '@terminal/react-query-hooks/graph/useGraphRelations';
import { useThemeCompanies } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ThemePerformanceSection } from '@terminal/views/theme-overview/theme-overview-drawer/components/theme-performance-section/ThemePerformanceSection';
import clsx from 'clsx';

import { FinancialDrawerContent } from '../financial-drawer-content/FinancialDrawerContent';
import { IndustryDrawerContent } from '../industry-drawer-content/IndustryDrawerContent';
import { MacroDrawerContent } from '../macro-drawer-content/MacroDrawerContent';
import { ThemeDrawerHeader } from '../theme-drawer-header/ThemeDrawerHeader';
import { TopCompaniesSection } from './components/top-companies-section/TopCompaniesSection';
import styles from './ThemeOverviewContent.module.scss';

const TOP_COMPANIES_LIMIT = 10;

export interface ThemeOverviewContentProps {
  source: PosthogTrackingSource;
  title?: string;
  tooltipThemeId?: string;
  theme: { theme: ThemeSchemaResponse };
}

export const ThemeOverviewContent = ({
  theme,
  source,
  title,
  tooltipThemeId,
}: ThemeOverviewContentProps) => {
  const themeDetails = theme.theme;
  const themeType = themeDetails.type;
  const isMacroFinancial = themeType === 'macro' || themeType === 'financial';

  const isDrawerOverhaulEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5200-drawer-overhaul')
  );
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const enabledDrawerOverhaul = isDrawerOverhaulEnabled && !isBasicUser;

  const {
    data: themeCompanies = [],
    isLoading: isThemeCompaniesLoading,
    error: themeCompaniesError,
  } = useThemeCompanies({
    themeId: theme.theme.id,
    enabled: !isMacroFinancial,
  });

  const {
    data: graphRelations,
    isLoading: isGraphRelationsLoading,
    error: graphRelationsError,
  } = useGraphRelations({
    node: theme.theme.id,
    nodeType: 'theme',
    relationKind: 'theme-company',
    direction: 'outbound',
    enabled: isMacroFinancial,
  });

  const macroFinancialCompanies: ThemeCompany[] =
    graphRelations?.edges
      .filter(
        edge =>
          edge.attributes.type === 'theme-company-financial' ||
          edge.attributes.type === 'theme-company-macro'
      )
      .flatMap(edge => {
        const company = edge.target.split('/')[1];
        if (!company) {
          return [];
        }

        return [
          {
            company,
            relationship: {
              id: edge.key,
              description: edge.attributes.description ?? '',
              rank: edge.attributes.rank,
              source_evidence: edge.attributes.source_evidence,
              evidence: edge.attributes.evidence,
            },
          },
        ];
      }) ?? [];

  const isLoading = isMacroFinancial
    ? isGraphRelationsLoading
    : isThemeCompaniesLoading;
  const error = isMacroFinancial ? graphRelationsError : themeCompaniesError;

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '700px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  if (error) {
    throw error;
  }

  const data = isMacroFinancial ? macroFinancialCompanies : themeCompanies;
  const entityTags = data
    .map(item => item.company)
    .slice(0, TOP_COMPANIES_LIMIT);
  const slicedData = data.slice(0, TOP_COMPANIES_LIMIT);
  const remainingCount = Math.max(0, data.length - TOP_COMPANIES_LIMIT);

  const oldContent = (
    <>
      <ErrorBoundaryWrapper>
        <ThemePerformanceSection
          themeId={theme.theme.id}
          entityTags={entityTags}
        />
      </ErrorBoundaryWrapper>
      {!!slicedData.length && (
        <TopCompaniesSection
          companies={slicedData}
          remainingCount={remainingCount}
          source={source}
          tooltipThemeId={tooltipThemeId}
        />
      )}
    </>
  );

  const renderHeader = () => {
    if (enabledDrawerOverhaul) {
      return <ThemeDrawerHeader theme={themeDetails} source={source} />;
    }

    return <ThemeHeaderDetailed theme={theme.theme} title={title} />;
  };

  const renderContent = () => {
    if (!enabledDrawerOverhaul) {
      return oldContent;
    }

    switch (themeType) {
      case 'industry':
        return <IndustryDrawerContent themeId={themeDetails.id} />;
      case 'financial':
        return <FinancialDrawerContent theme={themeDetails} />;
      case 'macro':
        return <MacroDrawerContent theme={themeDetails} />;
      default:
        return oldContent;
    }
  };

  return (
    <div
      className={clsx(styles.root, {
        [styles.perfDrawerFinancial]:
          themeType === 'financial' || themeType === 'macro',
      })}
    >
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
